import { PALocation } from "./location";
import { PAOperation } from "./operation";
import { PATechnicianDate } from "./technician-date";
import { PAUtil } from "./util";
import { PAProject } from "./project";
import { MapRoute, ProjectDayPercentage } from "../../../_models/planning-assistant.interface";
import { DayOpening } from "../../../_models/opening-times.interface";
import { AnimatableTour, TourAnimation } from "./tour-animation";
import { MapContainer } from "../map/pa-map";
import { FireUpdateOnChange } from "./util-classes/fire-update-on-change";
import * as mapboxgl from "mapbox-gl";
import { Route as MapboxRoute } from "src/app/_services/mapbox.service";
import { TechnicianPosition } from "./technician-live-position";
import { PAMapControl } from "../singletons/pa-map-control";
import { PADataControl } from "../singletons/pa-data-control";
import { PATimeControl } from "../singletons/pa-time-control";
import { PATourPlannerControl } from "../singletons/pa-tourplanner-control";
import { PAFilterControl } from "../singletons/pa-filter-control";
import { HolidaysTypes } from "date-holidays";

export interface DrivingTime {
  duration: number,
  distance: number,
  geometry: {
    coordinates: number[][]
  }
  skip_location_change: boolean
}

export interface TourRoute {
  start_location: PALocation
  end_location: PALocation
  operations: PAOperation[],
  driving_times: DrivingTime[],
  time_specific_data: {
    planned: TimeData,
    average: TimeData
  },
  marker_collection: { markers: { type: string, index: string }[], percent: number }[]
  driving_kms: number
}

export interface TimeData {
  project_work_times: { project: PAProject, amount: number }[]
  operation_work_time: number,
  driving_time: number,
  total_time: number,
  workload_percent: number,
  project_percentages: ProjectDayPercentage[]
  operation_data: OperationData[],
  home_driving: {
    timeline_position: TimeLinePosition,
  }
}

export interface OperationData {
  operation_time_strings: { travel_start: string, on_site: string, finished: string },
  timeline_positions: {
    driving: TimeLinePosition,
    operation: TimeLinePosition
  }
}

export interface TimeLinePosition {
  left_percent: number,
  right_percent: number,
  middle_percent?: number,
  additional_time_percent?: number
}

export interface RouteComparison {
  equal_operations: boolean,
  equal_operation_order: boolean,
  equal_operation_times: boolean,
}

export class PATour extends FireUpdateOnChange<'start_location_changed' | 'end_location_changed' | 'route_order_changed'> implements AnimatableTour {

  static timelineStart = 6 // 6AM
  static timelineEnd = 22 // 10PM
  private _maxWorkTime = 480

  private _color: { r: number, g: number, b: number, a?: number } = {r: 255, g: 120, b: 31, a: 1}
  private _operations: PAOperation[] = []
  public lunchBreak: { after_operation_idx: number, duration_minutes: number }
  public operationPriorityCountsBeforeOperation: { operation: PAOperation, count: number }[] = []
  public mapTourAnimations = new Map<MapContainer, TourAnimation>()
  public liveTechnicianPosition: TechnicianPosition

  public route: TourRoute

  public updating: boolean = false
  private queueUpdate: boolean = false


  public localPublicHolidays: HolidaysTypes.Holiday[]
  public localRegionalHolidays: HolidaysTypes.Holiday[]

  public workloadClass: string
  public workloadString: string

  constructor(
    readonly technicianDate: PATechnicianDate,
    private _startLocation: PALocation,
    private _endLocation: PALocation
  ) {
    super()

    const utc_ts = this.technicianDate.day.utc_timestamp
    this.localPublicHolidays = this.technicianDate.technician.getLocalPublicHolidays(utc_ts)
    this.localRegionalHolidays = this.technicianDate.technician.getLocalRegionalHolidays(utc_ts)

    this.resetRoute()
    this.liveTechnicianPosition = new TechnicianPosition(this)

  }

  get operations(): PAOperation[] {
    return this.route.operations
  }

  get startLocation(): PALocation {
    return this._startLocation
  }

  set startLocation(location: PALocation) {
    this._startLocation = location
    this.updateRoute(true)
    //this.fireUpdateManually('start_location_changed')
  }

  get endLocation(): PALocation {
    return this._endLocation
  }

  set endLocation(location: PALocation) {
    this._endLocation = location
    this.updateRoute(true)
    //this.fireUpdateManually('end_location_changed')
  }

  executeBeforeChange(): void {
  }

  getColor(): string {
    return PAUtil.rgbaToString(PAUtil.getIndexColorRGBA(PATimeControl.Instance.weekDays.indexOf(this.technicianDate.day.week_day)))
  }

  /*updateLocations(): void {
    this.updateStartLocation()
    this.updateEndLocation()
  }

  async waitForFirstLocationUpdate(): Promise<void> {
    while (!this.initializedLocations.start || !this.initializedLocations.end) {
      await PAUtil.sleep(100)
    }
  }
*/
  public async getMBRoutes(): Promise<MapboxRoute[]> {
    return this.operations.length ? [
        await this.startLocation.getDistanceToLocation(this.operations[0].ticket.client.location),
        ...await Promise.all(
          this.operations.map(
            async op => {
              const next_op_idx = this.operations.indexOf(op) + 1
              const next_location = this.operations.length <= next_op_idx ? this.endLocation : this.operations[next_op_idx].ticket.client.location
              return await op.ticket.client.location.getDistanceToLocation(next_location)
            }
          )
        )
      ] : [
        await this.startLocation.getDistanceToLocation(this.endLocation)
      ];
  }

  public async getMBRouteCoordinates(): Promise<number[][]> {
    const mb_routes = await this.getMBRoutes()
    return mb_routes.reduce((coordinates: number[][], route) => { return coordinates.concat(route.geometry.coordinates)}, [])
  }

  public async removeOperationWithId(id: number, wait_for_all_queued_updates?: boolean): Promise<void> {
    this._operations = this._operations.filter(operation => operation.id != id)
    await this.updateRoute(wait_for_all_queued_updates)
  }

  /*public updateStartLocation() {
    let current_start_location = this.getStartLocation()
    if (current_start_location != this.startLocation) {
      this.startLocation = current_start_location
      if (this == this.technicianDate.tour) {
        PATourPlannerControl.Instance.lastMainRouteLocationChangeTimestamp = Date.now()
      }
    }
    this.initializedLocations.start = true
  }

  public updateEndLocation() {
    let current_end_location = this.getEndLocation()
    if (current_end_location != this.endLocation) {
      this.endLocation = current_end_location
      if (this == this.technicianDate.tour) {
        PATourPlannerControl.Instance.lastMainRouteLocationChangeTimestamp = Date.now()
      }
    }
    this.initializedLocations.end = true
  }

  private getStartLocation(): PALocation {
    let previous_technician_date = this.technicianDate.getPreviousTechnicianDate()
    if (this.technicianDate.previousTravelTechnicianDate?.isActive() && previous_technician_date) {
      return this.technicianDate.previousTravelTechnicianDate.location || (previous_technician_date.tour?.operations?.length ? previous_technician_date.tour.operations[previous_technician_date.tour.operations.length - 1].ticket.client.location : this.technicianDate.technician.location)
    } else {
      return this.technicianDate.technician.location
    }
  }

  private getEndLocation(): PALocation {
    if (this.technicianDate.travelTechnicianDate?.isActive()) {
      return this.technicianDate.travelTechnicianDate.location || (this._operations.length ? this._operations[this._operations.length - 1].ticket.client.location : this.technicianDate.technician.location)
    } else {
      return this.technicianDate.technician.location
    }
  }*/

  public async insertOperations(insert_operations: PAOperation[], config?: {
    wait_for_all_queued_updates?: boolean,
    skip_route_update?: boolean,
    set_end_location_to_last_operation?: boolean
  }): Promise<void> {

    for (const insert_operation of insert_operations) {
      if (this._operations.filter(operation => operation.id == insert_operation.id).length == 0) {
        let idx = 0
        for (const i of PAUtil.range(0, this._operations.length - 1)) {
          if (this._operations[idx].startsBeforeOperation(insert_operation)) {
            idx = i + 1
          } else {
            break
          }
        }

        this._operations.splice(idx, 0, insert_operation)
      }
    }

    if (config?.set_end_location_to_last_operation && this._operations.length) {
      this._endLocation = this._operations[this._operations.length - 1].ticket.client.location
    }

    if (!config?.skip_route_update) {
      await this.updateRoute(config?.wait_for_all_queued_updates)
    }
  }

  public getRouteIdxForOperationWithId(id: number): number {
    return this.operations.findIndex(op => op.id == id)
  }

  public async updateOperationSorting(wait_for_all_queued_updates?: boolean): Promise<void> {
    this._operations.sort((op_a, op_b) => op_a.getStartTime() < op_b.getStartTime() ? -1 : 1)
    await this.updateRoute(wait_for_all_queued_updates)
  }

  async updateRoute(wait_for_all_queued_updates?: boolean) {

    if (!this.queueUpdate) {
      if (this.updating) {
        this.queueUpdate = true
        await PAUtil.sleepUntilConditionBreaks(
          () => {
            return this.updating
          }
        )
        this.queueUpdate = false
      }
    }

    this.updating = true
    await this.updateRouteData()
    this.updating = false

    if (wait_for_all_queued_updates) {
      await PAUtil.sleepUntilConditionBreaks(
        () => {
          return this.updating
        }
      )
    }
  }

  updateWorkloadInfo(): void {
    this.updateWorkloadClass()
    this.updateWorkloadString()
  }

  async updateRouteData(): Promise<void> {

    let driving_kms: number

    if (this.technicianDate.loadingStatus == 'init') {
      await this.technicianDate.loadData()
    }
    await this.technicianDate.setTravelTechnicianDate();

    const travel_data = await this.initTechnicianDateTravelData()
    driving_kms = this.getTotalDrivingKms()
    const project_work_times = await this.getProjectWorkTimes(travel_data.route_operations)

    const time_specific_data: { planned: TimeData, average: TimeData } = {
      planned: await this.getTimeSpecificData(travel_data, 'planned', project_work_times.planned),
      average: await this.getTimeSpecificData(travel_data, 'average', project_work_times.average)
    }

    const marker_collection = this.getSummarizedOpeningMarkerTimelinePositions(travel_data.route_operations)

    this.route.driving_kms = driving_kms
    this.route.driving_times = travel_data.driving_times
    this.route.operations = travel_data.route_operations
    this.route.time_specific_data = time_specific_data
    this.route.marker_collection = marker_collection

    this.liveTechnicianPosition.update()
    this.updateWorkloadInfo()

    if (this == this.technicianDate.tour) {
      const cw = this.technicianDate.getCalendarWeek()
      this.technicianDate.technician.updateCalendarWeekWorkload(`${cw.number}-${cw.year}`)
    }
  }

  async getTimeSpecificData(travel_data: {
    driving_times: { duration: number; distance: number }[];
    route_operations: PAOperation[]
  }, time_filter: 'planned' | 'average', project_work_times: {
    project: PAProject,
    amount: number
  }[]): Promise<TimeData> {

    const group_times = this.getTechnicianDateWorkTime(project_work_times)
    const project_day_percentages: ProjectDayPercentage[] = this.getProjectDayPercentages(project_work_times)

    return {
      driving_time: group_times.driving_time,
      operation_work_time: group_times.work_time,
      total_time: group_times.total_time,
      project_work_times: project_work_times,
      project_percentages: project_day_percentages,
      workload_percent: this.getWorkloadInPercent(group_times.total_time, true),
      operation_data: await this.getOperationData(travel_data, time_filter),
      home_driving: {
        timeline_position: this.getHomeDrivingTimelinePosition()
      }
    }
  }

  async getOperationData(travel_data: {
    driving_times: { duration: number; distance: number }[];
    route_operations: PAOperation[]
  }, filter: string): Promise<OperationData[]> {
    const operations_time_strings = await PATour.getOperationTimeStrings(travel_data, filter, PATimeControl.Instance)
    return travel_data.route_operations.map(op => {
      const idx = travel_data.route_operations.indexOf(op)
      return {
        operation_time_strings: operations_time_strings[idx],
        timeline_positions: {
          driving: op.getDrivingTimelinePosition(travel_data.driving_times[idx].duration, this.technicianDate.day.local_timestamp),
          operation: op.getTimelinePosition(this.technicianDate.day.local_timestamp)
        }
      }
    })
  }

  private async getProjectWorkTimes(operations: PAOperation[]) {
    const planned_work_times: { project: PAProject, amount: number }[] = []
    const average_work_times: { project: PAProject, amount: number }[] = []

    const operations_in_past = operations.filter(operation => operation.date_repaired && operation.date_on_site)
    const operations_in_future = operations.filter(operation => !(operation.date_repaired && operation.date_on_site))

    // Calc work_time for technician dates from the past
    if (operations_in_past.length) {
      const operation_with_travel_start_date = [...operations_in_past].filter(operation => operation.date_travel_start)
      const operations_sorted_by_travel_start = [...operation_with_travel_start_date].sort(
        (op_a, op_b) => (
          PATimeControl.Instance.dateTimeToDayMinutes(op_a.date_travel_start) >
          PATimeControl.Instance.dateTimeToDayMinutes(op_b.date_travel_start)
        ) ? 1 : -1)

      for (const operation of operations_sorted_by_travel_start) {
        const work_time = (PATimeControl.Instance.dateStringToTimestamp(operation.date_repaired, false, false) - PATimeControl.Instance.dateStringToTimestamp(operation.date_on_site, false, false)) / 60000
        const project = operation.getProject()
        planned_work_times.push({project: project, amount: work_time})
        average_work_times.push({project: project, amount: work_time})
      }
    }

    // Calc planned and average work_time for technician dates from the future
    if (operations_in_future.length > 0) {
      for (const operation of operations_in_future) {
        const technician = this.technicianDate.technician

        let priority_rule_time = 0
        const time_rule = technician ? operation.getAdditionalPriorityTimeRule({use_technician: technician}) : null
        if (time_rule) {
          priority_rule_time = Math.round(operation.ticket.time_estimation * operation.getAdditionalPriorityTimeRuleTimeFactor({use_technician: technician}))
        }

        const estimated_operation_time = operation.ticket.time_estimation
        let average_operation_time: number
        if (!technician.operationTimeEntryForPriorityWasLoaded(operation.ticket.priority.id)) {
          await technician.loadPriorityBasedOperationTimes(operation.ticket.priority.id)
        }
        const project = operation.getProject()
        average_operation_time = technician.getPriorityBasedAverageOperationTime(operation.ticket.priority.id)
        planned_work_times.push({project: project, amount: priority_rule_time || estimated_operation_time})
        average_work_times.push({
          project: project,
          amount: priority_rule_time || (average_operation_time >= 0 ? average_operation_time : estimated_operation_time)
        })
      }
    }

    return {'planned': planned_work_times, 'average': average_work_times}
  }

  public getProjectWorkTimesOfTechnicianDate(sort_by_size: boolean, project_work_times: {
    project: PAProject,
    amount: number
  }[]) {
    const work_times: { project: PAProject, time: number }[] = []
    for (const single_work_time of project_work_times) {
      const project = single_work_time.project
      let add_project_to_work_times = true
      for (const accumulated_work_time of work_times) {
        if (accumulated_work_time.project == project) {
          accumulated_work_time.time += single_work_time.amount
          add_project_to_work_times = false
          break
        }
      }
      if (add_project_to_work_times) {
        work_times.push({project: project, time: single_work_time.amount})
      }
    }
    if (sort_by_size) {
      work_times.sort((work_time_a, work_time_b) => (
        work_time_a.time <
        work_time_b.time
      ) ? 1 : -1)
    }
    return work_times
  }

  public getTotalDrivingTime() {
    const driving_times = [...this.route.driving_times]
    return driving_times.reduce((sum, driving_time) => sum + driving_time.duration, 0)
  }

  public getTotalDrivingKms() {
    const driving_times = [...this.route.driving_times]
    return driving_times.reduce((sum, driving_time) => sum + driving_time.distance, 0)
  }

  public getTotalWorkTime(with_driving_time: boolean, operation_time_filter: 'average' | 'planned'): number {
    const time_data = this.route.time_specific_data[operation_time_filter]
    return with_driving_time ? time_data.total_time : time_data.operation_work_time
  }

  public getProjectDayPercentages(project_work_time: { project: PAProject, amount: number }[]) {
    const max_work_time = this._maxWorkTime
    const project_work_times = this.getProjectWorkTimesOfTechnicianDate(true, project_work_time)
    const driving_time = this.getTotalDrivingTime()
    const accumulated_work_times = project_work_times.reduce((sum, work_time) => sum + work_time.time, 0)

    const time_off = Math.max(max_work_time - (driving_time + accumulated_work_times), 0)
    const off_percentage = (time_off / max_work_time) * 100

    const day_percentages: ProjectDayPercentage[] = []

    for (const project_work_time of project_work_times) {
      const project_percentage = (project_work_time.time / accumulated_work_times) * (100 - off_percentage)
      if (project_percentage > 0) {
        const project = project_work_time.project
        day_percentages.push({
          project: project,
          percentage: project_percentage,
          tooltip: project.project_name
        })
      }
    }

    return day_percentages
  }

  getTechnicianDateWorkTime(project_work_times: { project: PAProject, amount: number }[]): {
    work_time: number,
    driving_time: number,
    total_time: number
  } {
    const selected_work_time = project_work_times.reduce((sum: number, time_data) => sum + time_data.amount, 0)
    const total_driving_time = this.getTotalDrivingTime()
    const rounded_work_time = Math.round(selected_work_time * 10) / 10
    return {
      work_time: rounded_work_time,
      driving_time: total_driving_time,
      total_time: rounded_work_time + total_driving_time
    }
  }

  public toMapRoutes(): MapRoute[] {
    const routes: MapRoute[] = []
    const start_coordinates = this.startLocation.coordinates
    const end_coordinates = this.endLocation.coordinates
    const technician = this.technicianDate.technician
    const technician_color = technician.color ? technician.getColorString(1) : ''

    const location_change_operations = this._operations.filter(op => op.ticket.triggersLocationChange())

    if (location_change_operations.length) {
      const first_operation = location_change_operations[0]
      const color = technician_color || PAUtil.rgbaToString(this._color)
      let from_location = this.startLocation
      let to_location = first_operation.ticket.client.location
      let popup_text = `From Home to ${first_operation.ticket.address_company} (${first_operation.ticket.address_city})`
      routes.push({from: from_location, to: to_location, color: color, line_width: 4, dashed: false, popup_text})
      for (const operation of location_change_operations) {
        const idx = location_change_operations.indexOf(operation)
        from_location = operation.ticket.client.location
        if (idx == location_change_operations.length - 1) {
          to_location = this.endLocation
          popup_text = `From ${operation.ticket.address_company} (${operation.ticket.address_city}) to Home`
        } else {
          const next_operation = location_change_operations[idx + 1]
          to_location = next_operation.ticket.client.location
          popup_text = `From ${operation.ticket.address_company} (${operation.ticket.address_city}) to ${next_operation.ticket.address_company} (${next_operation.ticket.address_city})`
        }
        if (!(from_location.coordinates.latitude == to_location.coordinates.latitude && from_location.coordinates.longitude == to_location.coordinates.longitude)) {
          routes.push({from: from_location, to: to_location, color: color, line_width: 4, dashed: false, popup_text})
        }
      }
    } else if (start_coordinates.latitude != end_coordinates.latitude || start_coordinates.longitude != end_coordinates.longitude) {
      const popup_text = `From Home to Destination`
      routes.push({
        from: this.startLocation,
        to: this.endLocation,
        color: technician_color || PAUtil.rgbaToString(this._color),
        line_width: 4,
        dashed: false,
        popup_text: popup_text
      })
    }
    return routes
  }

  async initTechnicianDateTravelData(): Promise<{
    driving_times: DrivingTime[];
    route_operations: PAOperation[]
  }> {
    const travel_data = await this.updateTravelDurationsForTechnicianDate()
    await this.updatePriorityOperationCountsBeforeOperations(travel_data.route_operations)
    return travel_data
  }

  public async updateTravelDurationsForTechnicianDate() {
    this.resetRoute()

    const operations_changed = this.route.operations.length != this._operations.length || !this.route.operations.every(op => this.route.operations.indexOf(op) == this._operations.indexOf(op));

    const route_operations = operations_changed ? [...this._operations] : this.route.operations;
    const driving_times: DrivingTime[] = []

    if (route_operations.length) {
      for (const operation of route_operations) {
        const travel_data = await operation.getTravelDataToOperation(route_operations, this.startLocation)
        driving_times.push(travel_data)
      }

      const route_change_operations = route_operations.filter(op => op.ticket.triggersLocationChange())
      const last_route_change_operation = route_change_operations[route_change_operations.length - 1]
      const use_location_for_home_travel = last_route_change_operation ? last_route_change_operation.ticket.location : this.startLocation

      // fill in time to drive home for last operation
      const home_travel_data = await use_location_for_home_travel.getDistanceToLocation(this.endLocation)
      driving_times.push({duration: Math.round(home_travel_data.duration * this.technicianDate.technician.drivingTimeFactor), distance: home_travel_data.distance, geometry: home_travel_data.geometry, skip_location_change: false})
    } else if (
      this.startLocation.coordinates.latitude != this.technicianDate.technician.location.coordinates.latitude ||
      this.startLocation.coordinates.longitude != this.technicianDate.technician.location.coordinates.longitude ||
      this.endLocation.coordinates.longitude != this.technicianDate.technician.location.coordinates.longitude ||
      this.endLocation.coordinates.longitude != this.technicianDate.technician.location.coordinates.longitude
    ) {
      const home_travel_data = await this.startLocation.getDistanceToLocation(this.endLocation)
      driving_times.push({duration: Math.round(home_travel_data.duration * this.technicianDate.technician.drivingTimeFactor), distance: home_travel_data.distance, geometry: home_travel_data.geometry, skip_location_change: false})
    }

    this.route.operations = route_operations
    this.route.driving_times = driving_times

    return {route_operations, driving_times}
  }

  public resetRoute(): void {
    if (!this.route) {
      this.route = {
        start_location: this._startLocation,
        end_location: this._endLocation,
        driving_times: [],
        operations: [],
        driving_kms: 0,
        time_specific_data: {
          planned: {
            driving_time: 0,
            total_time: 0,
            operation_work_time: 0,
            project_percentages: [],
            project_work_times: [],
            workload_percent: 0,
            operation_data: [],
            home_driving: null
          },
          average: {
            driving_time: 0,
            total_time: 0,
            operation_work_time: 0,
            project_percentages: [],
            project_work_times: [],
            workload_percent: 0,
            operation_data: [],
            home_driving: null
          }
        },
        marker_collection: []
      }
    }
    this.updateWorkloadInfo()
  }

  public updateWorkloadClass(): void {
    const day_ts = this.technicianDate.day.utc_timestamp
    const holidays = this.technicianDate.technician.getLocalPublicHolidays(day_ts)
    if (holidays.length || this.technicianDate.absences.length || this.technicianDate.noShift) {
      this.workloadClass = 'red'
    } else {
      if (this.route) {
        const workload_percent = this.route.time_specific_data[PAFilterControl.Instance.selectedOperationTimeFilter].workload_percent
        if (25 > workload_percent) {
          this.workloadClass = 'green'
        } else if (75 > workload_percent) {
          this.workloadClass = 'yellow'
        } else {
          this.workloadClass = 'red'
        }
      } else {
        this.workloadClass = ''
      }
    }
  }

  updateWorkloadString(): void {
    const no_planned_tour_for_day = !this.operations.length
    if (this.localPublicHolidays?.length) {
      this.workloadString = 'Fei.'
    } else if (this.technicianDate.absences.length || this.technicianDate.noShift) {
      this.workloadString = 'Abw.'
    } else if (this.localRegionalHolidays.length && no_planned_tour_for_day) {
      this.workloadString = 'Reg. Fei?'
    } else {
      if (this.route) {
        this.workloadString = this.route.time_specific_data[PAFilterControl.Instance.selectedOperationTimeFilter].workload_percent + '%'
      } else {
        this.workloadClass = ''
      }
    }
  }


  public getWorkloadInPercent(workload_time: number, cut_at_100_percent?: boolean): number {
    const percentage = (workload_time / this._maxWorkTime) * 100
    return cut_at_100_percent ? Math.round(Math.min(percentage, 100)) : percentage
  }

  public updateTourMarkers(): void {
    this.technicianDate.technician.fireUpdateManually()
    this._operations.map(operation => operation.getStore() ? operation.getStore().fireUpdateManually() : {})
  }

  public centerInMap(): void {
    const map = PAMapControl.Instance.mainMapContainer.map
    const padding = 100
    const bounds = new mapboxgl.LngLatBounds();

    this._operations.map(operation => bounds.extend([operation.ticket.coordinates.longitude, operation.ticket.coordinates.latitude]))
    bounds.extend([this.startLocation.coordinates.longitude, this.startLocation.coordinates.latitude])
    bounds.extend([this.endLocation.coordinates.longitude, this.endLocation.coordinates.latitude])
    if (!bounds.isEmpty()) {
      map.fitBounds(
        bounds,
        {
          padding: {top: padding, bottom: padding, left: padding, right: padding},
          speed: 1,
          maxZoom: 13,
          minZoom: map.getZoom()
        }
      )
    }
  }

  public async updatePriorityOperationCountsBeforeOperations(operations: PAOperation[]): Promise<void> {
    this.operationPriorityCountsBeforeOperation = await Promise.all(operations.map(async op => {
      return {
        operation: op,
        count: await this.technicianDate.technician.getPriorityOperationCountUntilTimestamp(op.ticket.priority, op.getPlannedTimestamp())
      }
    }))
  }

  getPriorityOperationCountsBeforeOperation(operation: PAOperation) {
    const pcbo = this.operationPriorityCountsBeforeOperation.find(pcbo => pcbo.operation.id == operation.id)
    return pcbo ? pcbo.count : 0
  }

  getHomeDrivingTimelinePosition(): TimeLinePosition {

    const timeline_start = PATour.timelineStart
    const timeline_end = PATour.timelineEnd
    const timeline_start_ms = Math.floor(timeline_start) * 60 * 60 * 1000
    const timeline_end_ms = Math.ceil(timeline_end) * 60 * 60 * 1000
    const total_mss = timeline_end_ms - timeline_start_ms

    if (this.operations.length) {
      const last_operation = this.operations[this.operations.length - 1]

      let delay_for_lunch_break_minutes = 0
      if (Number.isInteger(this.lunchBreak?.after_operation_idx) && this.lunchBreak.after_operation_idx == this.operations.length - 1) {
        delay_for_lunch_break_minutes = this.lunchBreak.duration_minutes
      }

      if (last_operation.getPlannedDate() && last_operation.user_ids.length && !PADataControl.Instance.unassignedOperationUserIds.includes(last_operation.user_ids[0])) {

        const calculated_driving_time = this.route.driving_times[this.route.driving_times.length - 1].duration

        const driving_start_ms = last_operation.calculateOperationDayEndMilliseconds({use_day_timestamp: this.technicianDate.day.local_timestamp}) + delay_for_lunch_break_minutes * 60 * 1000
        const driving_end_ms = driving_start_ms + calculated_driving_time * 60 * 1000

        const driving_start_since_day_start_ms = driving_start_ms - timeline_start_ms
        const driving_end_since_day_start_ms = driving_end_ms - timeline_start_ms

        const left_percent = (driving_start_since_day_start_ms / total_mss) * 100
        const right_percent = 100 - (driving_end_since_day_start_ms / total_mss) * 100

        return {left_percent: left_percent, right_percent: right_percent}
      } else {
        return {left_percent: 100, right_percent: 100}
      }
    } else if (this.route.driving_times.length) {
      const calculated_driving_time = this.route.driving_times[0].duration
      const driving_start_ms = 1000 * 60 * 60 * 8
      const driving_end_ms = driving_start_ms + calculated_driving_time * 60 * 1000
      const driving_start_since_day_start_ms = driving_start_ms - timeline_start_ms
      const driving_end_since_day_start_ms = driving_end_ms - timeline_start_ms
      const left_percent = (driving_start_since_day_start_ms / total_mss) * 100
      const right_percent = 100 - (driving_end_since_day_start_ms / total_mss) * 100
      return {left_percent: left_percent, right_percent: right_percent}
    } else {
      return {left_percent: 100, right_percent: 100}
    }

  }

  getOpeningMarkerTimelinePositions(operations: PAOperation[]): {
    operation_idx: number,
    op_percent: number,
    cl_percent: number
  }[] {

    const timeline_start = PATour.timelineStart
    const timeline_end = PATour.timelineEnd

    const weekday = this.technicianDate.day.week_day
    const timeline_start_ms = Math.floor(timeline_start) * 60 * 60 * 1000
    const timeline_end_ms = Math.ceil(timeline_end) * 60 * 60 * 1000
    const total_mss = timeline_end_ms - timeline_start_ms

    const res: { operation_idx: number, op_percent: number, cl_percent: number }[] = []

    for (const operation of operations) {
      const weekday_opening = (operation.ticket.store_openings[weekday]) as DayOpening
      if (weekday_opening) {
        if (weekday_opening.validity == 'db') {
          const {
            open_percent,
            close_percent
          } = getWeekdayOpeningsOpenClosePercent(weekday_opening, timeline_start_ms, total_mss, PATimeControl.Instance);
          res.push({
            operation_idx: operations.indexOf(operation),
            op_percent: open_percent,
            cl_percent: close_percent
          })
        }
      }
    }

    return res
  }

  getSLAMarkerTimelinePositions(operations: PAOperation[]): { operation_idx: number, sla_percent: number }[] {

    const timeline_start = PATour.timelineStart
    const timeline_end = PATour.timelineEnd

    const timeline_start_ms = Math.floor(timeline_start) * 60 * 60 * 1000
    const timeline_end_ms = Math.ceil(timeline_end) * 60 * 60 * 1000
    const total_mss = timeline_end_ms - timeline_start_ms

    const res: { operation_idx: number, sla_percent: number }[] = []

    for (const operation of operations) {
      if (operation.ticket.datesla) {
        const day_timestamp = PATimeControl.Instance.dateStringToTimestamp(operation.ticket.datesla, true, true)
        if (this.technicianDate.day.utc_timestamp == day_timestamp) {
          const local_day_timestamp = PATimeControl.Instance.dateStringToTimestamp(operation.ticket.datesla, true, false)
          const sla_ms = Date.parse(operation.ticket.datesla) - local_day_timestamp
          const sla_since_day_start_ms = sla_ms - timeline_start_ms
          const sla_percent = (sla_since_day_start_ms / total_mss) * 100

          res.push({
            operation_idx: operations.indexOf(operation),
            sla_percent: sla_percent
          })
        }
      }
    }

    return res
  }

  getOutsideOpeningZonesTimelinePositions(config?: {
    for_user_with_id?: number,
    offset_percent?: number,
    offset_operations?: PAOperation[]
  }): { left_percent: number, right_percent: number, info: string, operation: PAOperation }[] {

    const timeline_start = PATour.timelineStart
    const timeline_end = PATour.timelineEnd
    const offset_percent = config?.offset_percent || 0
    const offset_operations = config?.offset_operations || []

    const res: { left_percent: number, right_percent: number, info: string, operation: PAOperation }[] = []

    for (const operation of this.operations) {

      const is_drag_and_drop_operation = offset_operations.includes(operation)
      const drag_and_drop_extra_percent = is_drag_and_drop_operation ? offset_percent : 0

      const operation_position = operation.getTimelinePosition(this.technicianDate.day.local_timestamp)
      const timeline_start_ms = Math.floor(timeline_start) * 60 * 60 * 1000
      const timeline_end_ms = Math.ceil(timeline_end) * 60 * 60 * 1000
      const total_mss = timeline_end_ms - timeline_start_ms

      if (operation.user_ids.length && !PADataControl.Instance.unassignedOperationUserIds.includes(operation.user_ids[0])) {

        const weekday_opening = (operation.ticket.store_openings[this.technicianDate.day.week_day]) as DayOpening
        if (weekday_opening) {
          if (weekday_opening.validity == 'db') {
            const {
              open_percent,
              close_percent
            } = getWeekdayOpeningsOpenClosePercent(weekday_opening, timeline_start_ms, total_mss, PATimeControl.Instance);

            if (operation_position.left_percent + drag_and_drop_extra_percent < open_percent) {
              res.push({
                left_percent: operation_position.left_percent + drag_and_drop_extra_percent,
                right_percent: Math.max(100 - open_percent, operation_position.right_percent - drag_and_drop_extra_percent),
                info: `Der Auftrag ${operation.ticket.address_company} liegt außerhalb der Öffnungszeiten`,
                operation: operation
              })
            }

            if (operation_position.right_percent - drag_and_drop_extra_percent < 100 - close_percent) {
              res.push({
                left_percent: Math.max(close_percent, operation_position.left_percent + drag_and_drop_extra_percent),
                right_percent: operation_position.right_percent - drag_and_drop_extra_percent,
                info: `Der Auftrag ${operation.ticket.address_company} liegt außerhalb der Öffnungszeiten`,
                operation: operation
              })
            }

          }
        } else {
          if (operation.hasOpenings()) {
            res.push({
              left_percent: operation_position.left_percent + drag_and_drop_extra_percent,
              right_percent: operation_position.right_percent - drag_and_drop_extra_percent,
              info: `Der Store ${operation.ticket.address_company} hat am ${PATimeControl.Instance.weekDayToGermanName(this.technicianDate.day.week_day, false)} nicht geöffnet.`,
              operation: operation
            })
          }
        }

        if (operation.ticket.datesla) {
          const day_timestamp = PATimeControl.Instance.dateStringToTimestamp(operation.ticket.datesla, true, true)
          if (this.technicianDate.day.utc_timestamp == day_timestamp) {
            const local_day_timestamp = PATimeControl.Instance.dateStringToTimestamp(operation.ticket.datesla, true, false)
            const sla_ms = Date.parse(operation.ticket.datesla) - local_day_timestamp
            const sla_since_day_start_ms = sla_ms - timeline_start_ms
            const sla_percent = (sla_since_day_start_ms / total_mss) * 100

            if (operation_position.right_percent - drag_and_drop_extra_percent < 100 - sla_percent) {
              res.push({
                left_percent: Math.max(sla_percent, operation_position.left_percent + drag_and_drop_extra_percent),
                right_percent: operation_position.right_percent - drag_and_drop_extra_percent,
                info: `Der Auftrag ${operation.ticket.address_company} liegt außerhalb der SLA-Zeit`,
                operation: operation
              })
            }
          } else if (this.technicianDate.day.utc_timestamp > day_timestamp) {
            res.push({
              left_percent: operation_position.left_percent + drag_and_drop_extra_percent,
              right_percent: operation_position.right_percent - drag_and_drop_extra_percent,
              info: `Der Auftrag ${operation.ticket.address_company} liegt außerhalb der SLA-Zeit`,
              operation: operation
            })
          }
        }
      }
    }

    return res
  }

  getSummarizedOpeningMarkerTimelinePositions(operations: PAOperation[]): {
    markers: { type: string, index: string }[],
    percent: number
  }[] {

    const percentMap = new Map<number, { type: string, index: string }[]>()
    for (const opening_marker of this.getOpeningMarkerTimelinePositions(operations)) {
      const index = `${(opening_marker.operation_idx + 1).toString()}`

      if (!percentMap.has(opening_marker.op_percent)) {
        percentMap.set(opening_marker.op_percent, [{type: 'O', index: index}])
      } else {
        percentMap.get(opening_marker.op_percent).push({type: 'O', index: index})
      }
      if (!percentMap.has(opening_marker.cl_percent)) {
        percentMap.set(opening_marker.cl_percent, [{type: 'C', index: index}])
      } else {
        percentMap.get(opening_marker.cl_percent).push({type: 'C', index: index})
      }
    }

    for (const opening_marker of this.getSLAMarkerTimelinePositions(operations)) {
      const index = `${(opening_marker.operation_idx + 1).toString()}`

      if (!percentMap.has(opening_marker.sla_percent)) {
        percentMap.set(opening_marker.sla_percent, [{type: 'SLA', index: index}])
      } else {
        percentMap.get(opening_marker.sla_percent).push({type: 'SLA', index: index})
      }
    }

    return [...percentMap.keys()].map(key => {
      return {markers: percentMap.get(key), percent: key}
    })
  }

  compareRoute(route: PAOperation[]): RouteComparison {

    const equal_operations = PAUtil.equalSets(new Set(this.route.operations.map(op => op.id)), new Set(route.map(op => op.id)))

    if (equal_operations) {
      let all_ids_match = true
      let all_times_match = true

      for (const idx of Array.from({length: this.operations.length}, (_, i) => i)) {
        const old_operation = this.operations[idx]
        const new_operation = route[idx]

        const ids_match = old_operation.id == new_operation.id

        if (ids_match) {
          const operation_date_match = Date.parse(old_operation.getPlannedDate()) == Date.parse(new_operation.getPlannedDate())
          const operation_time_match = old_operation.calculateOperationTimeMilliseconds() == new_operation.calculateOperationTimeMilliseconds()
          if (!operation_date_match || !operation_time_match) {
            all_times_match = false
          }
        } else {
          all_ids_match = false
        }
      }

      if (!all_ids_match) {
        return { equal_operations: true, equal_operation_order: false, equal_operation_times: false }
      } else if (!all_times_match) {
        return { equal_operations: true, equal_operation_order: true, equal_operation_times: false }
      } else {
        return { equal_operations: true, equal_operation_order: true, equal_operation_times: true }
      }
    } else {
      return { equal_operations: false, equal_operation_order: false, equal_operation_times: false }
    }
  }

  addLunchBreak(after_operation_idx: number, duration: number): void {
    this.lunchBreak = {after_operation_idx: after_operation_idx, duration_minutes: duration}
  }

  removeLunchBreak(): void {
    this.lunchBreak = null
  }

  public async initMapTourAnimation(map_container: MapContainer): Promise<void> {
    if (!this.mapTourAnimations.has(map_container)) {
      this.mapTourAnimations.set(map_container, new TourAnimation(this, map_container.map))
    }
    await this.mapTourAnimations.get(map_container).initAnimation()
    console.log(`Started tour animation ${this.technicianDate.day.week_day}-${this.technicianDate.technician.getFullName()}-${map_container.name}`)
  }

  public stopMapTourAnimation(map_container: MapContainer): void {
    const tour_animation = this.mapTourAnimations.get(map_container)
    if (tour_animation) tour_animation.deleteCurrentLayers()
    console.log(`Stopped tour animation ${this.technicianDate.day.week_day}-${this.technicianDate.technician.getFullName()}-${map_container.name}`)
  }

  public moveTourAnimationLayersUp(map_container: MapContainer): void {
    const tour_animation = this.mapTourAnimations.get(map_container)
    if (tour_animation) tour_animation.moveLastTourLayersUp()
  }

  private static async getOperationTimeStrings(travel_data: {
    driving_times: { duration: number; distance: number }[];
    route_operations: PAOperation[]
  }, filter: string, time_service: PATimeControl) {
    const time_strings: { travel_start: string, on_site: string, finished: string }[] = []

    for (const operation of travel_data.route_operations) {
      const technician_date = operation.getTechnicianDate()
      if (technician_date) {
        const op_idx = travel_data.route_operations.indexOf(operation)
        const operation_time_data_promise = operation.getOperationTimeData(technician_date.technician, travel_data.driving_times[op_idx].duration)
        const local_day_timestamp = time_service.dateToTimestamp(new Date(operation.getPlannedDate()), true, false)

        let travel_start = ''
        let on_site = ''
        let finished = ''

        if (operation.date_travel_start) {
          travel_start = operation.travelStartTimestring()
        } else {
          const operation_time_data = await operation_time_data_promise
          travel_start = time_service.dateToTimestring(new Date(local_day_timestamp + operation_time_data.driving_start_time * 60 * 1000), false)
        }

        if (operation.date_on_site) {
          on_site = operation.onSiteTimestring()
        } else {
          const operation_time_data = await operation_time_data_promise
          on_site = time_service.dateToTimestring(new Date(local_day_timestamp + operation_time_data.operation_start_time * 60 * 1000), false)
        }

        if (operation.date_finished) {
          finished = operation.finishedTimestring()
        } else {
          const operation_time_data = await operation_time_data_promise
          const operation_end_time = filter == 'average' ? operation_time_data.average_end_time : operation_time_data.estimated_end_time
          finished = time_service.dateToTimestring(new Date(local_day_timestamp + operation_end_time * 60 * 1000), false)
        }
        time_strings.push({travel_start: travel_start, on_site: on_site, finished: finished})
      }
    }
    return time_strings;
  }

  getLunchBreakTimelinePosition(config?: { offset_percent?: number }): {
    left_percent: number,
    right_percent: number,
    middle_percent: number
  } {

    const timeline_start = PATour.timelineStart
    const timeline_end = PATour.timelineEnd

    const fallback = {left_percent: 100, right_percent: 100, middle_percent: 50}

    if (this.lunchBreak && this.operations.length && this.lunchBreak.after_operation_idx < this.operations.length) {

      const timeline_start_ms = Math.floor(timeline_start) * 60 * 60 * 1000
      const timeline_end_ms = Math.ceil(timeline_end) * 60 * 60 * 1000
      const total_mss = timeline_end_ms - timeline_start_ms

      const after_operation = this.operations[this.lunchBreak.after_operation_idx]

      if (!after_operation) return fallback

      let start_ms = after_operation.calculateOperationDayEndMilliseconds({use_day_timestamp: this.technicianDate.day.local_timestamp})

      // Heutige Touren: Eine Pause nach einem bereits verstrichenen Auftrag (z. B. vergangener
      // Fixtermin) beginnt frühestens „jetzt" — sie kann nicht in der Vergangenheit liegen.
      // Auf volle Minuten gerundet, damit das Style-Binding zwischen zwei Change-Detection-Läufen
      // stabil bleibt (sonst NG0100-Fehlerflut durch millisekundengenaue Positionswerte)
      const ts_now = Date.now()
      if (this.technicianDate.day.utc_timestamp < ts_now && ts_now < this.technicianDate.day.utc_timestamp + 24 * 60 * 60 * 1000) {
        const now_day_minute_ms = Math.floor((ts_now - PATimeControl.Instance.dateToTimestamp(new Date(), true, false)) / 60000) * 60000
        if (now_day_minute_ms > start_ms) {
          start_ms = now_day_minute_ms
        }
      }

      const end_ms = start_ms + this.lunchBreak.duration_minutes * 60000

      const operation_start_since_day_start_ms = start_ms - timeline_start_ms
      const operation_end_since_day_start_ms = end_ms - timeline_start_ms

      const offset_percent = config?.offset_percent || 0

      const left_percent = (operation_start_since_day_start_ms / total_mss) * 100 + offset_percent
      const right_percent = 100 - (operation_end_since_day_start_ms / total_mss) * 100 - offset_percent
      const middle_percent = (left_percent + (100 - right_percent)) / 2

      return {left_percent: left_percent, right_percent: right_percent, middle_percent: middle_percent}
    } else {
      return fallback
    }

  }

  static getLunchBreakTimeConfig(tour_minutes: number): { minutes: number, after_tour_minute: number } {
    if (tour_minutes > 6 * 60) {
      if (tour_minutes > 9 * 60) {
        return {minutes: 45, after_tour_minute: 270}
      } else {
        return {minutes: 30, after_tour_minute: 180}
      }
    }
  }

  getTimeColor(time_filter: string): string {
    const total_time = this.route.time_specific_data[time_filter].total_time
    if (total_time <= 420) {
      return 'orange'
    } else if (total_time <= 540) {
      return 'greenyellow'
    } else if (total_time <= 600) {
      return 'orange'
    } else {
      return 'red'
    }
  }

  getTourAnimationColor(): string {
    let is_in_selected_technician_week_tours = false
    const id = PATourPlannerControl.Instance.tourPlannerPlanningMode == 'technician' ? PATourPlannerControl.Instance.technicianToPlanID : null
    if (id) {
      const technician = PADataControl.Instance.getTechnician(id)
      is_in_selected_technician_week_tours = technician.getCurrentCalendarWeekTechnicianDates().includes(this.technicianDate)
    }
    return is_in_selected_technician_week_tours ? PAUtil.rgbaToString(PAUtil.getIndexColorRGBA(this.technicianDate.getWeekdayIdx())) : PAUtil.rgbaToString(PAUtil.getIndexColorRGBA(this.technicianDate.getWeekdayIdx()))
  }

  getTourIndexForOperationWithId(id: number) {
    return this.operations.findIndex(op => op.id == id)
  }
}

function getWeekdayOpeningsOpenClosePercent(weekday_opening: DayOpening, timeline_start_ms: number, total_mss: number, time_service: PATimeControl) {
  const open_ms = time_service.timeStringToMinutes(weekday_opening.open) * 60 * 1000
  const close_ms = time_service.timeStringToMinutes(weekday_opening.close) * 60 * 1000

  const open_since_day_start_ms = open_ms - timeline_start_ms
  const close_since_day_start_ms = close_ms - timeline_start_ms

  const open_percent = (open_since_day_start_ms / total_mss) * 100
  const close_percent = (close_since_day_start_ms / total_mss) * 100
  return {open_percent, close_percent};
}
